import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { h2hAPI } from '../api/endpoints';
import { FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreateH2H.css';

const CreateH2H = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    home_team: '',
    away_team: '',
    kickoff_time: '',
    minimum_stake: '',
    title: '',
  });

  const isPunterPlus = user?.punter_plus || false;

  React.useEffect(() => {
    if (!isPunterPlus) {
      toast.error('You need Punter+ to create H2H games');
      navigate('/subscription/upgrade');
    }
  }, [isPunterPlus, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.home_team || !formData.away_team || !formData.kickoff_time || !formData.minimum_stake) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await h2hAPI.create({
        ...formData,
        minimum_stake: parseFloat(formData.minimum_stake),
        title: formData.title || `${formData.home_team} vs ${formData.away_team}`,
      });
      toast.success('H2H game created successfully!');
      navigate(`/h2h/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create H2H game');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-h2h-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="create-h2h-card">
        <div className="card-header">
          <h1>Create H2H Game</h1>
          <p className="card-subtitle">Challenge others in a head-to-head match</p>
          <span className="punter-badge">⭐ Punter+</span>
        </div>

        <form onSubmit={handleSubmit} className="create-h2h-form">
          {/* Title */}
          <div className="form-group">
            <label>Title (Optional)</label>
            <input
              type="text"
              name="title"
              placeholder="e.g., El Clasico H2H"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          {/* Teams */}
          <div className="form-row">
            <div className="form-group">
              <label>Home Team *</label>
              <input
                type="text"
                name="home_team"
                placeholder="e.g., Real Madrid"
                value={formData.home_team}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Away Team *</label>
              <input
                type="text"
                name="away_team"
                placeholder="e.g., Barcelona"
                value={formData.away_team}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Kickoff Time */}
          <div className="form-group">
            <label>Kickoff Time *</label>
            <input
              type="datetime-local"
              name="kickoff_time"
              value={formData.kickoff_time}
              onChange={handleChange}
              required
            />
          </div>

          {/* Minimum Stake */}
          <div className="form-group">
            <label>Minimum Stake ($XPUNT) *</label>
            <input
              type="number"
              name="minimum_stake"
              placeholder="e.g., 5.00"
              value={formData.minimum_stake}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              required
            />
            <small className="hint">Minimum amount required to join</small>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Create H2H Game'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateH2H;